'use client';

const DB_NAME = 'conference-chat-cache-v1';
const STORE_NAME = 'messages';
const DB_VERSION = 1;
const CONVERSATION_INDEX = 'conversationCacheKey';

export interface CachedChatMessageRecord {
  cacheKey: string;
  conversationCacheKey: string;
  userGuid: string;
  conferenceYear: number;
  conversationId: string;
  conversationHash: string;
  messageId: string;
  plaintext: string;
  sendTime: string;
  senderRegion: number;
  senderType: number;
  senderSourceID: string;
  receiverRegion: number;
  receiverType: number;
  receiverSourceID: string;
  fromSelf: boolean;
  updatedAt: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

const canUseIndexedDb = () =>
  typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';

const openChatCacheDb = () => {
  if (!canUseIndexedDb()) {
    return Promise.reject(new Error('IndexedDB is unavailable'));
  }
  if (dbPromise) {
    return dbPromise;
  }
  dbPromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () =>
      reject(request.error || new Error('open chat cache failed'));
    request.onupgradeneeded = () => {
      const db = request.result;
      const store = db.objectStoreNames.contains(STORE_NAME)
        ? request.transaction?.objectStore(STORE_NAME)
        : db.createObjectStore(STORE_NAME, {
            keyPath: 'cacheKey',
          });
      if (store && !store.indexNames.contains(CONVERSATION_INDEX)) {
        store.createIndex(CONVERSATION_INDEX, CONVERSATION_INDEX, {
          unique: false,
        });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
  return dbPromise;
};

export const buildChatConversationCacheKey = (
  userGuid: string,
  conferenceYear: number,
  conversationId: string,
  conversationHash: string
) =>
  [
    userGuid.trim(),
    String(conferenceYear || 0),
    conversationHash.trim() || conversationId.trim() || 'draft',
  ].join('__');

export const buildChatMessageCacheKey = (
  conversationCacheKey: string,
  messageId: string
) => `${conversationCacheKey}__${messageId.trim()}`;

export const listCachedChatMessages = async (
  conversationCacheKey: string
): Promise<CachedChatMessageRecord[]> => {
  if (!canUseIndexedDb()) {
    return [];
  }
  const db = await openChatCacheDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index(CONVERSATION_INDEX);
    const request = index.getAll(IDBKeyRange.only(conversationCacheKey));
    request.onerror = () =>
      reject(request.error || new Error('list cached chat messages failed'));
    request.onsuccess = () => {
      // 明文缓存只做当前浏览器内的会话回显和弱离线兜底，不参与服务端状态判断。
      const list = (request.result || []) as CachedChatMessageRecord[];
      list.sort((left, right) => {
        if (left.sendTime === right.sendTime) {
          return left.messageId.localeCompare(right.messageId);
        }
        return left.sendTime.localeCompare(right.sendTime);
      });
      resolve(list);
    };
  });
};

export const saveCachedChatMessages = async (
  records: CachedChatMessageRecord[]
) => {
  if (!canUseIndexedDb() || !records.length) {
    return;
  }
  const db = await openChatCacheDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    transaction.onerror = () =>
      reject(transaction.error || new Error('save cached chat messages failed'));
    transaction.oncomplete = () => resolve();
    records.forEach((record) => {
      // 同一条消息按 messageId 幂等覆盖，避免会话刷新时重复写入本地明文。
      store.put(record);
    });
  });
};
